import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Activity,
  Calendar,
  Download,
  Loader2,
  Target
} from "lucide-react";

const TOOLTIP_STYLE = {
  backgroundColor: 'hsl(var(--card))',
  border: '1px solid hsl(var(--border))',
  borderRadius: '8px'
};

import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend
} from "recharts";

const COLORS = ['#10B981', '#EF4444', '#F59E0B'];

export default function AnalyticsPage() {
  const { token } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [failureTrends, setFailureTrends] = useState([]);
  const [confidenceData, setConfidenceData] = useState(null);
  const [retryData, setRetryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchAnalytics = useCallback(async () => {
    try {
      const [analyticsRes, trendsRes, confRes, retryRes] = await Promise.all([
        axios.get(`${API}/analytics`, { headers }),
        axios.get(`${API}/analytics/failure-trends?days=30`, { headers }),
        axios.get(`${API}/analytics/confidence-distribution?days=30`, { headers }),
        axios.get(`${API}/analytics/retry-history?days=30`, { headers }).catch(() => ({ data: [] }))
      ]);
      setAnalytics(analyticsRes.data);
      setFailureTrends(trendsRes.data);
      setConfidenceData(confRes.data);
      setRetryData(retryRes.data);
    } catch (error) {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/analytics/export-excel`, {
        headers,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `verifypro_export_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel report downloaded");
    } catch (error) {
      toast.error("Failed to export");
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-12">
        <BarChart3 className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
        <p className="text-muted-foreground">No analytics data available</p>
      </div>
    );
  }

  // Prepare chart data
  const pieData = [
    { name: 'Passed', value: analytics.passed_tests, color: '#10B981' },
    { name: 'Failed', value: analytics.failed_tests, color: '#EF4444' },
    { name: 'In Progress', value: analytics.total_tests - analytics.passed_tests - analytics.failed_tests, color: '#F59E0B' }
  ].filter(d => d.value > 0);

  const trendData = analytics.tests_by_date.map(item => ({
    date: item.date.slice(5), // MM-DD format
    passed: item.passed || 0,
    failed: item.failed || 0,
    total: (item.passed || 0) + (item.failed || 0) + (item.in_progress || 0)
  })).reverse();

  const productData = analytics.tests_by_product.map(item => ({
    name: item.product.length > 15 ? item.product.slice(0, 15) + '...' : item.product,
    passed: item.passed,
    failed: item.failed,
    total: item.total
  }));

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Analytics</h2>
          <p className="text-muted-foreground">Verification performance insights and trends</p>
        </div>
        <Button onClick={handleExportExcel} disabled={exporting} variant="outline">
          {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
          Export Excel
        </Button>
      </div>

      {/* Key Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Tests</CardTitle>
            <Activity className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-mono">{analytics.total_tests}</div>
            <p className="text-xs text-muted-foreground mt-1">All time verifications</p>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Pass Rate</CardTitle>
            {analytics.pass_rate >= 80 ? (
              <TrendingUp className="w-4 h-4 text-emerald-500" />
            ) : (
              <TrendingDown className="w-4 h-4 text-red-500" />
            )}
          </CardHeader>
          <CardContent>
            <div className={`text-3xl font-bold font-mono ${
              analytics.pass_rate >= 80 ? 'text-emerald-600' : 
              analytics.pass_rate >= 50 ? 'text-amber-600' : 'text-red-600'
            }`}>
              {analytics.pass_rate}%
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {analytics.pass_rate >= 80 ? 'Excellent' : (analytics.pass_rate >= 50 ? 'Needs improvement' : 'Critical')}
            </p>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Passed</CardTitle>
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-mono text-emerald-600">{analytics.passed_tests}</div>
            <p className="text-xs text-muted-foreground mt-1">Successfully verified</p>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Failed</CardTitle>
            <XCircle className="w-4 h-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-mono text-red-600">{analytics.failed_tests}</div>
            <p className="text-xs text-muted-foreground mt-1">Requires attention</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Pass/Fail Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Test Distribution</CardTitle>
            <CardDescription>Pass/Fail breakdown of all tests</CardDescription>
          </CardHeader>
          <CardContent>
            {pieData.length > 0 ? (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={TOOLTIP_STYLE} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                No test data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tests Over Time */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Tests Over Time
            </CardTitle>
            <CardDescription>Daily verification activity (last 7 days)</CardDescription>
          </CardHeader>
          <CardContent>
            {trendData.length > 0 ? (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <Tooltip contentStyle={TOOLTIP_STYLE} />
                    <Legend />
                    <Line type="monotone" dataKey="passed" stroke="#10B981" strokeWidth={2} dot={{ r: 4 }} />
                    <Line type="monotone" dataKey="failed" stroke="#EF4444" strokeWidth={2} dot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                No trend data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Product Performance */}
      {productData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Performance by Product</CardTitle>
            <CardDescription>Verification results breakdown by product type</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={productData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis type="category" dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={12} width={120} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                  <Bar dataKey="passed" fill="#10B981" name="Passed" radius={[0, 4, 4, 0]} />
                  <Bar dataKey="failed" fill="#EF4444" name="Failed" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Common Failure Points */}
      {analytics.failure_by_step?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Common Failure Points
            </CardTitle>
            <CardDescription>Verification steps with the highest failure rates</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {analytics.failure_by_step.map((item, index) => {
                const maxFailures = Math.max(...analytics.failure_by_step.map(f => f.failures));
                const percentage = (item.failures / maxFailures) * 100;
                
                return (
                  <div key={`step-fail-${item.step}-${index}`} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm">{item.step}</span>
                      <Badge variant="destructive" className="font-mono">
                        {item.failures} failures
                      </Badge>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-red-500 rounded-full transition-all duration-500"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step Failure Trends */}
      {failureTrends.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-red-500" />
              Step Failure Rates (Last 30 Days)
            </CardTitle>
            <CardDescription>Which steps fail most often — focus improvement efforts here</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {failureTrends.slice(0, 15).map((step, index) => {
                const passRate = step.total > 0 ? ((step.pass / step.total) * 100).toFixed(1) : 0;
                return (
                  <div key={`trend-${step.step_name}-${index}`} className="space-y-1.5">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium truncate max-w-[300px]">{step.step_name}</span>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="text-emerald-600">{step.pass} pass</span>
                        <span className="text-red-600">{step.fail} fail</span>
                        {step.warning > 0 && <span className="text-amber-600">{step.warning} warn</span>}
                        {step.skipped > 0 && <span className="text-slate-500">{step.skipped} skip</span>}
                        <Badge variant={step.fail_rate > 30 ? "destructive" : step.fail_rate > 10 ? "secondary" : "outline"} className="font-mono text-xs w-20 justify-center">
                          {step.fail_rate}% fail
                        </Badge>
                      </div>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden flex">
                      <div className="h-full bg-emerald-500 transition-all" style={{ width: `${passRate}%` }} />
                      <div className="h-full bg-red-500 transition-all" style={{ width: `${step.fail_rate}%` }} />
                      {step.warning > 0 && <div className="h-full bg-amber-400 transition-all" style={{ width: `${(step.warning / step.total * 100)}%` }} />}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* AI Confidence Distribution */}
      {confidenceData && confidenceData.total_verifications > 0 && (
        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-blue-500" />
                AI Confidence Distribution
              </CardTitle>
              <CardDescription>
                Average: {confidenceData.avg_confidence}% across {confidenceData.total_verifications} verifications
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={confidenceData.distribution}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="range" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <Tooltip contentStyle={TOOLTIP_STYLE} />
                    <Bar dataKey="count" name="Verifications" radius={[4, 4, 0, 0]}>
                      {confidenceData.distribution.map((entry, i) => (
                        <Cell key={i} fill={
                          entry.range === "96-100" ? "#10B981" :
                          entry.range === "91-95" ? "#34D399" :
                          entry.range === "81-90" ? "#6EE7B7" :
                          entry.range === "61-80" ? "#FCD34D" :
                          entry.range === "41-60" ? "#F59E0B" :
                          entry.range === "21-40" ? "#EF4444" : "#DC2626"
                        } />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Per-Step Confidence</CardTitle>
              <CardDescription>Steps sorted by average AI confidence (lowest first)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2.5 max-h-[300px] overflow-y-auto pr-2">
                {confidenceData.per_step.map((step, i) => (
                  <div key={i} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="truncate max-w-[200px] font-medium">{step.step_name}</span>
                      <div className="flex items-center gap-2">
                        {step.needs_more_references && (
                          <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">Needs refs</Badge>
                        )}
                        <Badge variant={step.avg_confidence >= 80 ? "default" : step.avg_confidence >= 60 ? "secondary" : "destructive"} className="font-mono text-xs w-16 justify-center">
                          {step.avg_confidence}%
                        </Badge>
                      </div>
                    </div>
                    <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${step.avg_confidence >= 80 ? "bg-emerald-500" : step.avg_confidence >= 60 ? "bg-amber-400" : "bg-red-500"}`} style={{ width: `${step.avg_confidence}%` }} />
                    </div>
                  </div>
                ))}
              </div>
              {confidenceData.steps_needing_references?.length > 0 && (
                <div className="mt-4 p-3 rounded-lg border bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/20">
                  <p className="text-xs font-medium text-amber-700 dark:text-amber-400">
                    {confidenceData.steps_needing_references.length} step(s) have low confidence — upload more reference images for: {confidenceData.steps_needing_references.slice(0, 3).join(", ")}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Retry Analytics */}
      {retryData.length > 0 && retryData.some(r => r.tests_with_retries > 0) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Steps Requiring Retries (Last 30 Days)
            </CardTitle>
            <CardDescription>Steps where QC engineers had to re-verify — indicates assembly or AI issues</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {retryData.filter(r => r.tests_with_retries > 0).slice(0, 10).map((step, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
                  <div>
                    <p className="font-medium text-sm">{step.step_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {step.tests_with_retries} of {step.total_tests} tests needed retries
                      {step.avg_retries_when_retried > 1 ? ` (avg ${step.avg_retries_when_retried} retries)` : ""}
                    </p>
                  </div>
                  <Badge variant={step.retry_rate > 30 ? "destructive" : step.retry_rate > 15 ? "secondary" : "outline"} className="font-mono">
                    {step.retry_rate}% retry rate
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Insights */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Insights</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="p-4 rounded-lg border bg-emerald-500/5 border-emerald-500/20">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                <span className="font-medium">Best Performing</span>
              </div>
              <p className="text-sm text-muted-foreground">
                {analytics.pass_rate >= 90 
                  ? "Excellent quality control! Maintain these standards."
                  : analytics.pass_rate >= 70
                  ? "Good pass rate. Focus on reducing failures in critical steps."
                  : "Room for improvement. Review failing steps carefully."}
              </p>
            </div>

            <div className="p-4 rounded-lg border bg-amber-500/5 border-amber-500/20">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                <span className="font-medium">Attention Needed</span>
              </div>
              <p className="text-sm text-muted-foreground">
                {analytics.failure_by_step?.[0]
                  ? `"${analytics.failure_by_step[0].step}" has the most failures. Consider reviewing the process.`
                  : "No significant failure patterns detected."}
              </p>
            </div>

            <div className="p-4 rounded-lg border bg-blue-500/5 border-blue-500/20">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="w-5 h-5 text-blue-500" />
                <span className="font-medium">Activity</span>
              </div>
              <p className="text-sm text-muted-foreground">
                {analytics.total_tests > 0
                  ? `${analytics.total_tests} total tests conducted. Keep up the quality checks!`
                  : "Start running verification tests to see analytics."}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
